import { useMemo } from "react";
import { merge } from "lodash";
import { parseJSON } from "date-fns";
import { keepPreviousData } from "@tanstack/react-query";
import { TwrorResult } from "@rentablo/api/apiv1";
import { addAreaValues, getInterval } from "@helpers";
import {
  calculatePercentsForPerformance,
  SecuritiesChartDataWithDate,
  SecuritiesChartSelectedType,
} from "@components";
import { useInvestmentHistoricQuotes } from "@features/investments/investment/hooks/useInvestmentHistoricQuotes";
import { mergeComparisonInvestmentByDate } from "@features/investments/investment/utils/mergeQuotes";
import { usePerformanceByAssetClass } from "./usePerformanceByAssetClass";
import { AssetClassInvestmentType } from "./assetClasses";

const convertToChartData = <T extends { date: Date }>(
  fieldName: string,
  data?: TwrorResult[]
): T[] => {
  if (!data) return [];
  return data.flatMap(({ date, portfolioValue }) =>
    date
      ? [
          {
            date: parseJSON(date),
            [fieldName]: Math.round((portfolioValue ?? 0) * 100) / 100,
          } as unknown as T,
        ]
      : []
  );
};

export const convertToPercentChartData = <T extends { date: Date }>(
  fieldName: string,
  data?: TwrorResult[]
): T[] => {
  if (!data) return [];
  return data.flatMap(({ date, twror }) =>
    date
      ? [
          {
            date: parseJSON(date),
            [fieldName]: twror,
          } as unknown as T,
        ]
      : []
  );
};

export interface InvestmentsChartDataStatistic {
  portfolioValue: number;
  performance: number;
  changeToday: number;
  investedCapital: number;
  changeTodayAbs: number;
  valuationGain: number;
  valuationGainPercent: number;
  irr: number;
}

export const useInvestmentsChartData = (
  accountIds: number[],
  startDate?: Date,
  endDate?: Date,
  chartType: "performance" | "valuation" = "valuation",
  comparedTicker?: string,
  displayCurrency?: string,
  // An asset class filter: the chart and the statistic show only these
  // investment types, combined by the backend.
  investmentTypes: AssetClassInvestmentType[] = []
) => {
  const isValuationChart = chartType === "valuation";
  const interval = getInterval(startDate, endDate);
  const isFiltered = investmentTypes.length > 0;

  const query = usePerformanceByAssetClass({
    accountIds,
    startDate,
    endDate,
    interval,
    displayCurrency,
  });
  const filteredQuery = usePerformanceByAssetClass({
    accountIds,
    startDate,
    endDate,
    interval,
    displayCurrency,
    combinedAssetClasses: investmentTypes,
  });
  const source = isFiltered ? filteredQuery : query;
  // The per class lines only exist for the whole portfolio.
  const classes = isFiltered ? undefined : query;
  const converterCallback = isValuationChart
    ? convertToChartData
    : convertToPercentChartData;

  const totalPerformance = converterCallback<{
    date: Date;
    totalValue: number;
  }>("totalValue", source.total?.twrorResults);

  const allHoldings = converterCallback<{
    date: Date;
    holdingsValue: number;
  }>("holdingsValue", classes?.allHoldings?.twrorResults);

  const fonds = converterCallback<{
    date: Date;
    fondsValue: number;
  }>("fondsValue", classes?.funds?.twrorResults);

  const etf = converterCallback<{
    date: Date;
    etfsValue: number;
  }>("etfsValue", classes?.etfs?.twrorResults);

  const derivative = converterCallback<{
    date: Date;
    derivativeValue: number;
  }>("derivativeValue", classes?.derivative?.twrorResults);

  const bonds = converterCallback<{
    date: Date;
    bondsValue: number;
  }>("bondsValue", classes?.bonds?.twrorResults);

  const crypto = converterCallback<{
    date: Date;
    cryptoValue: number;
  }>("cryptoValue", classes?.crypto?.twrorResults);

  const mergedData: SecuritiesChartDataWithDate[] = merge(
    totalPerformance,
    allHoldings,
    fonds,
    etf,
    derivative,
    bonds,
    crypto
  );

  const {
    data: comparisonHistoryQuotes = [],
    isPending: historicQuotesIdle,
    isLoading: historicQuotesLoading,
  } = useInvestmentHistoricQuotes(
    {
      tickerSymbol: comparedTicker,
      interval,
      from: startDate?.toISOString(),
      to: endDate?.toISOString(),
    },
    {
      staleTime: Infinity,
      enabled: Boolean(comparedTicker),
      placeholderData: keepPreviousData,
    }
  );

  const chartData = comparedTicker
    ? mergeComparisonInvestmentByDate(mergedData, comparisonHistoryQuotes)
    : mergedData;

  const data = addAreaValues(chartData, "totalValue");

  const holdingsHasData = query.allHoldings?.hasData;
  const fundsHasData = query.funds?.hasData;
  const etfsHasData = query.etfs?.hasData;
  const derivativeHasData = query.derivative?.hasData;
  const bondsHasData = query.bonds?.hasData;
  const cryptoHasData = query.crypto?.hasData;

  const typesWithData = useMemo<SecuritiesChartSelectedType[]>(() => {
    const result: SecuritiesChartSelectedType[] = [];
    if (holdingsHasData) result.push("holdings");
    if (fundsHasData) result.push("funds");
    if (etfsHasData) result.push("etfs");
    if (derivativeHasData) result.push("derivative");
    if (bondsHasData) result.push("bonds");
    if (cryptoHasData) result.push("crypto");
    return result;
  }, [
    holdingsHasData,
    fundsHasData,
    etfsHasData,
    derivativeHasData,
    bondsHasData,
    cryptoHasData,
  ]);

  const total = source.total;
  const twrorResult = total?.twrorResults[total.twrorResults.length - 1];
  const cashFlowResult =
    total?.cashFlowResults[total.cashFlowResults.length - 1];
  const irrResult = total?.irrResults[total.irrResults.length - 1];

  const portfolioValue = twrorResult?.portfolioValue ?? 0;
  const performance = (twrorResult?.twror ?? 1) - 1;
  const changeToday = total?.changeToday?.performance ?? 0;
  const investedCapital = cashFlowResult?.investedCapital ?? 0;
  const changeTodayAbs = total?.changeToday?.changeAbsCashFlowAdjusted ?? 0;
  const valuationGain = portfolioValue - investedCapital;
  const valuationGainPercent = portfolioValue / investedCapital - 1;
  const irr = irrResult?.irr ?? 0;

  const statistic: InvestmentsChartDataStatistic = useMemo(
    () => ({
      portfolioValue,
      performance,
      changeToday,
      investedCapital,
      changeTodayAbs,
      valuationGain,
      valuationGainPercent,
      irr,
    }),
    [
      portfolioValue,
      performance,
      changeToday,
      investedCapital,
      changeTodayAbs,
      valuationGain,
      valuationGainPercent,
      irr,
    ]
  );

  return {
    data: isValuationChart
      ? data
      : calculatePercentsForPerformance(data, Boolean(comparedTicker)),
    typesWithData,
    statistic: statistic ?? {},
    responseCurrency: source.responseCurrency,
    isPending: source.isPending && historicQuotesIdle,
    isLoading: source.isLoading || historicQuotesLoading,
    isFetching: source.isFetching,
  };
};
